import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import {
	useCategories,
	useCurrentStore,
	usePaginatedSearch,
} from "../../hooks";

const Categories = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("categories.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { searchQuery, page, setPage, onSearchQueryChange } =
		usePaginatedSearch();
	const { data, isLoading } = useCategories({
		page,
		"q[nameOrCategoryNameCont]": searchQuery,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": ["name"],
	});

	const columnHelper = createColumnHelper<DataTableFeatures, Category>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor("name", {
					header: t("categories.table.name"),
					cell: (props) => (
						<Link
							className="underline"
							to={`/categories/${props.row.original.id}/edit`}
						>
							{props.getValue()}
						</Link>
					),
				}),
				columnHelper.accessor("category.name", {
					header: t("categories.table.parentCategory"),
				}),
				columnHelper.accessor("vatRate.ratePercentCents", {
					header: t("categories.table.vat"),
					cell: (props) => {
						const ratePercentCents = props.getValue();
						return ratePercentCents ? `${ratePercentCents / 100}%` : "";
					},
				}),
			] as ColumnDef<DataTableFeatures, Category>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("categories.searchPlaceholder")}
			onSearchQueryChange={onSearchQueryChange}
			searchQuery={searchQuery}
			isLoading={isLoading}
			createUrl="/categories/new"
			createLabel={t("categories.createButtonLabel")}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default Categories;
